import type { NavigationMap, NavPosition } from '../../../packages/sim/src/navigation/index.ts';

/** How many cells ahead a held direction looks for floor. */
const REACH = 4;
/** How far either side of the held direction a step may turn: about 45°, a little over so pressing into a level edge still slides along it. */
const SWEEP = Math.cos(55 * Math.PI / 180);
const floors = new WeakMap<NavigationMap, Set<string>>();

/**
 * The next cell for a held screen direction (WASD). The painted floors run on the
 * diagonal, so a straight screen step often leaves them: instead, look a few cells
 * out within about ±45° of the direction and take the first step toward the floor that
 * advances furthest along it. Holding a key follows a diagonal quay and slides along
 * its edges; it stops only where no floor continues that way.
 */
export function stride(map: NavigationMap, from: NavPosition, dx: number, dy: number): NavPosition | null {
  let cells = floors.get(map);
  if (!cells) floors.set(map, cells = new Set(map.layers.find(layer => layer.id === from.layer)?.cells.map(cell => `${cell.x}:${cell.y}`)));
  const size = map.cellSize, length = Math.hypot(dx, dy), ux = dx / length, uy = dy / length;
  const queue: [NavPosition, NavPosition | null, number][] = [[from, null, 0]], seen = new Set([`${from.x}:${from.y}`]);
  let best: [NavPosition, NavPosition] | null = null, score = 0;
  for (let i = 0; i < queue.length; i++) {
    const [at, first, depth] = queue[i]!;
    const ox = (at.x - from.x) / size, oy = (at.y - from.y) / size;
    const along = ox * ux + oy * uy, across = Math.abs(ox * uy - oy * ux);
    if (first && along >= Math.hypot(ox, oy) * SWEEP && along - across / 2 > score) { score = along - across / 2; best = [at, first]; }
    if (depth === REACH) continue;
    for (const [nx, ny] of [[1, 0], [0, 1], [-1, 0], [0, -1]] as const) {
      const next = { x: at.x + nx * size, y: at.y + ny * size, layer: from.layer }, key = `${next.x}:${next.y}`;
      if (seen.has(key) || !cells.has(key)) continue;
      seen.add(key); queue.push([next, first ?? next, depth + 1]);
    }
  }
  if (!best) return null;
  // Step along the axis with more of the way still to go; on an even diagonal, alternate by cell so W+D walks a
  // straight staircase rather than a long L. A wall on that step falls back to the first step of the found way.
  const [target, first] = best, rx = (target.x - from.x) / size, ry = (target.y - from.y) / size;
  const horizontal = Math.abs(rx) > Math.abs(ry) || Math.abs(rx) === Math.abs(ry) && ((from.x + from.y) / size) % 2 === 0;
  const step = horizontal ? { x: from.x + Math.sign(rx) * size, y: from.y, layer: from.layer } : { x: from.x, y: from.y + Math.sign(ry) * size, layer: from.layer };
  return cells.has(`${step.x}:${step.y}`) ? step : first;
}
